import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  InputAdornment,
  Paper,
  useMediaQuery,
  useTheme,
  Grid,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import PersonIcon from '@mui/icons-material/Person';
import { useNavigate } from 'react-router-dom';

import { signInWithPopup } from "firebase/auth";
import { auth, provider } from '../firebase';
import GoogleIcon from '@mui/icons-material/Google';
import classsoftLogo from '../assets/classsoft.png';

const API_BASE = process.env.REACT_APP_API_URL;

export default function LoginPage({ onLogin }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showNewUserForm, setShowNewUserForm] = useState(false);
  const [formData, setFormData] = useState({
    farmerId: '',
    name: '',
    mobile: '',
    email: '',
    dairyName: '',
    dairyContact: '',
    dairyPlace: '',
    taluka: '',
    district: ''
  });

  /* ------------------------ Helper: login handlers ------------------------ */
  const handleLogin = async () => {
    if (!username.trim() || !password.trim()) {
      alert("Please enter both Farmer Code and Password.");
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify({ username, password }),
      });

      let data;
      try {
        data = await response.json();
      } catch (jsonError) {
        console.error("Failed to parse JSON:", jsonError);
        throw new Error("Invalid response from server");
      }

      if (response.ok && data.farmer) {
        localStorage.setItem("farmerData", JSON.stringify(data.farmer));
        localStorage.setItem("farmerUsername", username);
        localStorage.setItem("farmerName", data.farmer.name);
        localStorage.setItem("DairyName", data.farmer.dairy_name);
        onLogin();
        // navigate("/passbook");

        // ✅ Determine companyId from username (A–J → 0–9 pattern)
          const parseCompanyId = (username) => {
            const letters = "ABCDEFGHIJ";
            const companyCode = username.split("-")[0] || "";
            let companyId = 0;
            for (let i = 0; i < companyCode.length; i++) {
              const idx = letters.indexOf(companyCode[i].toUpperCase());
              if (idx !== -1) companyId = companyId * 10 + idx;
            }
            return companyId;
          };

        const companyId = parseCompanyId(username);

        // ✅ Conditional navigation based on companyId
        if (companyId === 52) {
          navigate("/studentpassbook");
        } else {
          // navigate("/passbook");
          alert("Contact your administrator!");
        }
      


      } else {
        alert(data?.error || "Login failed. Please try again.");
      }
    } catch (error) {
      console.error("Login request error:", error);
      alert("Unable to connect to the server. Please try again later.");
    }

  };

  const handleGoogleLogin = async () => {
    if (!username.trim()) {
      alert("Please enter your Farmer Code before signing in with Google.");
      return;
    }

    try {
      const result = await signInWithPopup(auth, provider);
      const email = result.user.email;

      const response = await fetch(`${API_BASE}/google-login`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify({ username, google_id: email }),
      });

      const data = await response.json();

      if (response.ok && data.farmer) {
        localStorage.setItem("farmerData", JSON.stringify(data.farmer));
        localStorage.setItem("farmerUsername", username);
        onLogin();
        navigate("/passbook");
      } else {
        alert(data.error || "Google login failed. Please try again.");
      }
    } catch (error) {
      console.error("Google sign-in error:", error);
      alert("Google Sign-In failed. Please try again.");
    }
  };

  /* ------------------------ Helper: new user registration ------------------------ */
  const handleRegister = async () => {
    if (
      !formData.farmerId ||
      !formData.name ||
      !formData.mobile ||
      !formData.dairyName ||
      !formData.dairyContact ||
      !formData.dairyPlace ||
      !formData.taluka ||
      !formData.district
    ) {
      alert("Please fill all required fields.");
      return;
    }

    // ✅ FIXED: payload matches backend expectations
    const payload = {
      farmerId: formData.farmerId,
      name: formData.name,
      mobile: formData.mobile,
      email: formData.email,
      dairyName: formData.dairyName,
      dairyContact: formData.dairyContact,
      dairyPlace: formData.dairyPlace,
      taluka: formData.taluka,
      district: formData.district,
    };

    try {
      console.log("Registration request payload:", payload);

      // ✅ FIXED: endpoint corrected
      const response = await fetch(`${API_BASE}/farmers/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      console.log("Registration API Response:", data);

      if (response.ok) {
        alert(data.message || "Registration successful! You can now log in.");
        setShowNewUserForm(false);
        setFormData({
          farmerId: '',
          name: '',
          mobile: '',
          email: '',
          dairyName: '',
          dairyContact: '',
          dairyPlace: '',
          taluka: '',
          district: ''
        });
      } else {
        alert(data.error || "Registration failed. Please try again.");
      }
    } catch (error) {
      console.error("Registration request error:", error);
      alert("Unable to connect to the server. Please try again later.");
    }
  };

  /* ------------------------ Layout ------------------------ */
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        minHeight: '100vh',
        width: '100%',
        boxSizing: 'border-box',
        overflowY: 'auto',
      }}
    >
      {/* Left / Top (branding) */}
      <Box
        sx={{
          flexBasis: { xs: 'auto', sm: '40%' },
          flexShrink: 0,
          backgroundColor: '#660880ff',
          color: '#fff',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: { xs: 'flex-start', sm: 'center' },
          alignItems: 'center',
          py: { xs: 3, sm: 6 },
          px: { xs: 2, sm: 4 },
          textAlign: 'center',
        }}
      >
        <Box
          component="img"
          src={classsoftLogo}
          alt="ClassSoft"
          sx={{
            width: { xs: 110, sm: 200 },
            height: { xs: 110, sm: 200 },
            borderRadius: '50%',
            objectFit: 'cover',
            mt: { xs: 1, sm: 3 },
          }}
        />

        <Typography
          variant={isMobile ? 'h6' : 'h5'}
          sx={{ mt: { xs: 2, sm: 5 }, fontWeight: '700' }}
        >
          ClassSoft Student App
        </Typography>
      </Box>

      {/* Right / Bottom (form) */}
      <Box
        sx={{
          flex: 1,
          backgroundColor: '#fff',
          color: '#111',
          display: 'flex',
          justifyContent: 'center',
          alignItems: { xs: 'flex-start', sm: 'center' },
          py: { xs: 4, sm: 0 },
          px: { xs: 2, sm: 4 },
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: { xs: 3, sm: 4 },
            width: '100%',
            maxWidth: 420,
            borderRadius: 3,
            textAlign: 'center',
            boxSizing: 'border-box',
            mt: { xs: 3, sm: 0 },
          }}
        >
          <Typography variant="h5" sx={{ mb: 2, color: '#1a67dcff', fontWeight: 'bold' }}>
            ClassSoft Student App
          </Typography>

          {/* Login Inputs */}
          <TextField
            fullWidth
            label="Username"
            margin="normal"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonIcon />
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            label="Password"
            type="password"
            margin="normal"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon />
                </InputAdornment>
              ),
            }}
          />

          <Button
            fullWidth
            variant="contained"
            sx={{ mt: 2.5, backgroundColor: '#1a67dcff', color: 'white', py: 1.2 }}
            onClick={handleLogin}
          >
            LOGIN
          </Button>

          {/* <Typography sx={{ mt: 2, mb: 1 }}>or</Typography>

          <Button
            fullWidth
            variant="outlined"
            startIcon={<GoogleIcon />}
            onClick={handleGoogleLogin}
          >
            Sign in with Google
          </Button>
 */}
          {/* Toggle New User Form */}
          {/* <Typography
            sx={{ mt: 2, cursor: 'pointer', color: 'blue' }}
            onClick={() => setShowNewUserForm(!showNewUserForm)}
          >
            If New User, Click here
          </Typography> */}

          {/* New User Form */}
          {showNewUserForm && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>New User Registration</Typography>

              <TextField
                fullWidth
                label="Farmer Id"
                margin="normal"
                value={formData.farmerId}
                onChange={(e) => setFormData({ ...formData, farmerId: e.target.value })}
              />
              <TextField
                fullWidth
                label="Name"
                margin="normal"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
              <TextField
                fullWidth
                label="Mobile No."
                margin="normal"
                value={formData.mobile}
                onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
              />
              <TextField
                fullWidth
                label="Email Id"
                margin="normal"
                placeholder="Auto-detected from mobile device"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
              <TextField
                fullWidth
                label="Dairy Name"
                margin="normal"
                value={formData.dairyName}
                onChange={(e) => setFormData({ ...formData, dairyName: e.target.value })}
              />
              <TextField
                fullWidth
                label="Dairy Contact No."
                margin="normal"
                value={formData.dairyContact}
                onChange={(e) => setFormData({ ...formData, dairyContact: e.target.value })}
              />

              {/* Dairy Place & Taluka side-by-side on larger screens */}
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Dairy Place"
                    margin="normal"
                    value={formData.dairyPlace}
                    onChange={(e) => setFormData({ ...formData, dairyPlace: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Taluka"
                    margin="normal"
                    value={formData.taluka}
                    onChange={(e) => setFormData({ ...formData, taluka: e.target.value })}
                  />
                </Grid>
              </Grid>

              {/* District */}
              <TextField
                fullWidth
                label="District"
                margin="normal"
                value={formData.district}
                onChange={(e) => setFormData({ ...formData, district: e.target.value })}
              />

              <Button
                fullWidth
                variant="contained"
                sx={{ mt: 2, backgroundColor: '#004d40', color: 'white' }}
                onClick={handleRegister}
              >
                Apply
              </Button>
            </Box>
          )}

          <Typography variant="caption" sx={{ mt: 3, display: 'block' }}>
            Developed by e.Sense InfoSolutions
          </Typography>
        </Paper>
      </Box>
    </Box>
  );
}
